import "./App.css";
import { Provider as ReduxProvider } from "react-redux";
import { ChakraProvider } from "@chakra-ui/react";
import { BrowserRouter } from "react-router-dom";
import Router from "src/components/Router";
import store, { persistedStore } from "src/store";
import theme from "src/styles/theme";
import { PersistGate } from "redux-persist/integration/react";
import { useEffect } from "react";
import CacheBuster from "react-cache-buster";
import AppQueryClient from "./AppQueryClient";

// font-faces
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/500.css";
import "@fontsource/montserrat/600.css";
import "@fontsource/montserrat/700.css";
import "@fontsource/montserrat/800.css";
import "@fontsource/open-sans/300.css";
import "@fontsource/open-sans/400.css";
import "@fontsource/open-sans/500.css";
import "@fontsource/open-sans/600.css";
import "@fontsource/open-sans/700.css";
import "@fontsource/open-sans/800.css";
import "react-datepicker/dist/react-datepicker.css";
import AgreementPopup from "./components/modules/dashboard/AgreementPopup";
import BetaTermsPopup from "./components/modules/dashboard/BetaTermsPopup";
import LoginAsAlert from "./components/layout/LoginAsAlert";
import { useLoggedInAs } from "./store/loggedInAs";
import Pendo from "./components/analytics/Pendo";
import { cookieStorage } from "./utils/cookieStorage";
import LiveChat from "./components/supports/livechat/LiveChat";
import CallForwardingNotice from "./components/modules/abbyAiReceptionist/aiReceptionistOnboarding/CallForwardingNotice";
import IntercomLauncher from "./components/messenger/IntercomLauncher";
import CalendlyScheduleOnboarding from "./components/modules/abbyAiReceptionist/calendlyScheduleOnboarding/CalendlyScheduleOnboarding";

function LoggedInAsGate({ children }: { children: any }) {
  const { isAuthenticated } = useLoggedInAs();
  const { impersonatedBy } = useLoggedInAs();

  return (
    <>
      {isAuthenticated && impersonatedBy && <LoginAsAlert />}
      {children}
    </>
  );
}

function App() {
  useEffect(() => {
    const gtmId = process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID as string;
    if (gtmId) {
      const script = document.createElement("script");
      script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
      script.async = true;
      script.type = "text/javascript";
      document.head.appendChild(script);
    }

    console.log("APP VERSION:", process.env.REACT_APP_VERSION);
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "module";
    script.src =
      "https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.esm.js";
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Store entry point uri for post login navigation
  useEffect(() => {
    let pathname = window.location.pathname;
    if (pathname.includes("oauth/callback")) {
      pathname = "/settings/integrations";
    }
    if (window.location.search) {
      pathname += window.location.search;
    }
    cookieStorage.setItemSync("entry-point-uri", pathname);
  }, []);

  return (
    <CacheBuster
      currentVersion={process.env.REACT_APP_VERSION}
      isEnabled={true}
      isVerboseMode={false}
      metaFileDirectory={"."}
    >
      <ChakraProvider theme={theme}>
        <ReduxProvider store={store}>
          <AppQueryClient>
            <PersistGate loading={null} persistor={persistedStore}>
              <BrowserRouter>
                <CallForwardingNotice />
                <CalendlyScheduleOnboarding />
                <LoggedInAsGate>
                  <Router />
                  <Pendo />
                </LoggedInAsGate>
              </BrowserRouter>
              <IntercomLauncher />
              <AgreementPopup />
              <BetaTermsPopup />
            </PersistGate>
          </AppQueryClient>
        </ReduxProvider>
        <LiveChat />
      </ChakraProvider>
    </CacheBuster>
  );
}

export default App;
